import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Menu, MenuItem, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useFeature } from '../../context/VendorConfigContext';
import AlertBell from './AlertBell';

interface TabDef {
  label: string;
  mobileLabel?: string;
  path: string;
  matchPaths: string[];
}

const TABS: TabDef[] = [
  {
    label: 'Overview',
    path: '/vendor/finance',
    matchPaths: ['/vendor/finance', '/vendor/finance/dashboard'],
  },
  {
    label: 'Expenses',
    path: '/vendor/finance/expenses',
    matchPaths: ['/vendor/finance/expenses'],
  },
  {
    label: 'Income',
    path: '/vendor/finance/income',
    matchPaths: ['/vendor/finance/income'],
  },
  {
    label: 'Cash Register',
    mobileLabel: 'Cash',
    path: '/vendor/finance/cash',
    matchPaths: ['/vendor/finance/cash'],
  },
  {
    label: 'Payables',
    path: '/vendor/finance/payables',
    matchPaths: ['/vendor/finance/payables'],
  },
  {
    label: 'Staff',
    path: '/vendor/finance/staff',
    matchPaths: ['/vendor/finance/staff'],
  },
  {
    label: 'EOD Summary',
    mobileLabel: 'EOD',
    path: '/vendor/finance/eod-summary',
    matchPaths: ['/vendor/finance/eod-summary'],
  },
  {
    label: 'EOD History',
    mobileLabel: 'History',
    path: '/vendor/finance/eod-history',
    matchPaths: ['/vendor/finance/eod-history'],
  },
  {
    label: 'Aggregators',
    mobileLabel: 'Aggreg.',
    path: '/vendor/finance/aggregators',
    matchPaths: ['/vendor/finance/aggregators'],
  },
  {
    label: 'Import',
    path: '/vendor/finance/import',
    matchPaths: ['/vendor/finance/import'],
  },
  {
    label: 'Settings',
    mobileLabel: '⚙️',
    path: '/vendor/finance/settings',
    matchPaths: ['/vendor/finance/settings'],
  },
];

// Tabs visible to a finance-only-restricted steward. Anything else is hidden
// (Cash Register / Payables / Staff / Aggregators / Import / Settings) because
// the backend will 403 those endpoints anyway and we don't want dead nav.
const FINANCE_ONLY_TAB_LABELS = new Set(['Expenses', 'Income', 'EOD Summary']);

// Statutory tips distribution (UK plan Phase 4) — only offered where the
// feature flag is on (GB vendors), inserted after Staff.
const TIPS_TAB: TabDef = {
  label: 'Tips',
  path: '/vendor/tips',
  matchPaths: ['/vendor/tips'],
};

// Enterprise consolidation: the five destinations people hit daily stay as
// always-visible pills; everything else lives under one "More ▾" menu so the
// bar never wraps or crushes the alert bell. Two tiers by viewport:
// Aggregators outranks Cash Register in daily use (owner call, 2026-08-06),
// and wide screens can afford two more pills before the overflow.
const PRIMARY_TAB_LABELS_MOBILE = new Set([
  'Overview',
  'Expenses',
  'Income',
  'Aggregators',
  'EOD Summary',
]);
const PRIMARY_TAB_LABELS_DESKTOP = new Set([
  ...PRIMARY_TAB_LABELS_MOBILE,
  'Cash Register',
  'Payables',
]);

/* Shared pill styling — Tier-2 tokens only, real buttons, 40px min height. */
const pillSx = (active: boolean) => ({
  appearance: 'none',
  backgroundColor: active ? 'var(--bg-selected)' : 'transparent',
  color: active ? 'var(--action)' : 'var(--text-secondary)',
  fontWeight: active ? 600 : 500,
  border: active ? '1px solid var(--action)' : '1px solid var(--border-default)',
  borderRadius: '20px',
  px: { xs: '12px', sm: '16px' },
  py: 0,
  fontSize: '0.8125rem',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  fontFamily: 'inherit',
  lineHeight: 1.4,
  minHeight: '40px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  flexShrink: 0,
  outline: 'none',
  '&:focus-visible': {
    outline: '2px solid var(--focus-ring)',
    outlineOffset: '2px',
  },
});

const FinanceTabBar: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  const isFinanceOnly = !!user?.financeOnlySteward;
  const tipsEnabled = useFeature('tipsDistribution');
  const visibleTabs = isFinanceOnly
    ? TABS.filter((t) => FINANCE_ONLY_TAB_LABELS.has(t.label))
    : TABS.flatMap((t) => (t.label === 'Staff' && tipsEnabled ? [t, TIPS_TAB] : [t]));

  const isActive = (tab: TabDef): boolean =>
    tab.matchPaths.some((p) => location.pathname === p);

  const wideBar = useMediaQuery('(min-width: 1180px)');
  const PRIMARY_TAB_LABELS = wideBar ? PRIMARY_TAB_LABELS_DESKTOP : PRIMARY_TAB_LABELS_MOBILE;
  const primaryTabs = visibleTabs.filter((t) => PRIMARY_TAB_LABELS.has(t.label));
  const overflowTabs = visibleTabs.filter((t) => !PRIMARY_TAB_LABELS.has(t.label));
  const activeOverflowTab = overflowTabs.find(isActive);
  const moreOpen = Boolean(moreAnchor);

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        position: { xs: 'sticky', sm: 'static' },
        top: { xs: 0, sm: 'auto' },
        zIndex: { xs: 5, sm: 'auto' },
        backgroundColor: { xs: theme.background, sm: 'transparent' },
        py: { xs: 1, sm: 0 },
        mx: { xs: -1.5, sm: 0 },
        px: { xs: 1.5, sm: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '6px',
          overflowX: 'auto',
          pb: '2px',
          flex: 1,
          minWidth: 0,
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          WebkitMaskImage: {
            xs: 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)',
            sm: 'none',
          },
          maskImage: {
            xs: 'linear-gradient(to right, black calc(100% - 24px), transparent 100%)',
            sm: 'none',
          },
        }}
      >
        {primaryTabs.map((tab) => {
          const active = isActive(tab);
          const label = isMobile && tab.mobileLabel ? tab.mobileLabel : tab.label;

          return (
            <Box
              component="button"
              type="button"
              key={tab.path}
              onClick={() => navigate(tab.path)}
              sx={pillSx(active)}
            >
              {label}
            </Box>
          );
        })}

        {overflowTabs.length > 0 && (
          <Box
            component="button"
            type="button"
            data-testid="finance-more-tab"
            aria-haspopup="menu"
            aria-expanded={moreOpen ? 'true' : undefined}
            onClick={(e: React.MouseEvent<HTMLElement>) => setMoreAnchor(e.currentTarget)}
            sx={pillSx(Boolean(activeOverflowTab))}
          >
            {activeOverflowTab ? `More · ${activeOverflowTab.label}` : 'More'}
            <span aria-hidden="true" style={{ fontSize: '0.7em', lineHeight: 1 }}>
              {'▾'}
            </span>
          </Box>
        )}
      </Box>

      {/* Fixed right slot — the bell never scrolls, wraps, or gets clipped. */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          alignSelf: 'center',
          ml: 'auto',
        }}
      >
        <AlertBell />
      </Box>

      <Menu
        anchorEl={moreAnchor}
        open={moreOpen}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            minWidth: 180,
            borderRadius: 'var(--r-md)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
            '& .MuiList-root': { py: 0.5 },
          },
        }}
      >
        {overflowTabs.map((tab) => {
          const active = isActive(tab);
          return (
            <MenuItem
              key={tab.path}
              selected={active}
              onClick={() => {
                setMoreAnchor(null);
                navigate(tab.path);
              }}
              sx={{
                fontSize: '0.8125rem',
                minHeight: 40,
                color: active ? 'var(--action)' : 'var(--text-secondary)',
                fontWeight: active ? 600 : 500,
                '&.Mui-selected': {
                  backgroundColor: 'var(--bg-selected)',
                  '&:hover': { backgroundColor: 'var(--bg-selected)' },
                },
              }}
            >
              {tab.label}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default FinanceTabBar;
